import React from "react";
import useApi from "../../hooks/useGaneshApi";
import { useError } from "../../contexts/ErrorContext";
import { useQuery } from "@tanstack/react-query";

import { DeliveryHistoryRecord_T } from "../../types/Delivery";

import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";

import {
  getDateValue,
  getNumberValue,
  renderDateCell,
  renderDurationFromSecsCell,
  renderExternalLinkCell,
  renderFileSizeCell,
  renderHeaderWithLogo,
  renderNumberCell,
  renderPercentCell,
} from "../../utils/dataGrid";

import JsonDisplayModalWithFetch from "../Compliance/UI/JsonDisplayModalWithFetch";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import DisplayExportJobAnalysis from "../UI/DisplayExportJobAnalysis";
import DownloadS3URIButton from "../UI/DownloadS3URIButton";
import TableCellBooleanIcon from "../UI/TableCellBooleanIcon";

/*
  This component takes in a delivery id, and returns a data grid with the delivery history 
*/

interface DeliveryHistoryTableProps {
  deliveryId?: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "target_fulfillment_date", sort: "desc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
  aggregation: {
    model: {
      export_duration: "avg",
      export_id: "size",
    },
  },
};

const DeliveryHistoryTable: React.FC<DeliveryHistoryTableProps> = ({
  deliveryId,
}) => {
  const { showError } = useError();
  const { apiRequest } = useApi();
  const { data, isLoading, isError, error } = useQuery<
    DeliveryHistoryRecord_T[]
  >({
    queryKey: ["delivery-history", deliveryId],
    queryFn: async () => {
      const response = await apiRequest<DeliveryHistoryRecord_T[]>(
        `/v1/deliveries/${deliveryId}/history`,
        { method: "GET" }
      );
      return response.data || [];
    },
  });

  if (isError) {
    showError(`Failed to fetch delivery history: ${error.message}`);
  }

  const columns_def: GridColDef[] = [
    {
      field: "export_s3_uri",
      headerName: "Download Export",
      filterable: false,
      width: 110,
      renderCell: (params) =>
        params.value ? <DownloadS3URIButton s3Uri={params.value} /> : null,
    },
    {
      field: "export_summary",
      headerName: "View Export Summary",
      filterable: false,
      width: 110,
      sortable: false,
      valueGetter: (value, row) => row.export_id,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <JsonDisplayModalWithFetch
            fetchUrl={`/v1/exports/${params.value}/export_summary`}
            title="Export Summary"
            iconOnly={true}
            enableCollapse={true}
          />
        ) : null,
    },
    {
      field: "export_job_id",
      headerName: "View Export Job Details",
      filterable: false,
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <DisplayExportJobAnalysis exportJobId={params.value} />
        ) : null,
    },
    {
      field: "target_fulfillment_date",
      headerName: "Target Fulfillment Date",
      width: 160,
      type: "date",
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "export_id",
      aggregable: false,
      headerName: "Export ID",
      renderHeader: () => renderHeaderWithLogo("Export ID"),
      width: 150,
      type: "string",
      valueGetter: (value: string) =>
        value
          ? `https://kuebiko.dataplor.com/deliveries/${deliveryId}/exports/${value}`
          : null,
      renderCell: (params: GridRenderCellParams) =>
        params.rowNode.type == "pinnedRow"
          ? params.value // if aggregation row, show the value
          : params.value // else, check for value and render the link or default value
          ? renderExternalLinkCell(params, params.row.export_id)
          : "--",
    },
    {
      field: "export_completed_date",
      headerName: "Export Date",
      width: 160,
      type: "date",
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "export_duration",
      headerName: "Export Duration",
      width: 150,
      type: "number",
      valueGetter: getNumberValue,
      renderCell: renderDurationFromSecsCell,
    },
    {
      field: "fulfillment_completed_date",
      headerName: "Fulfillment Completed Date",
      width: 160,
      type: "date",
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "completion_signal",
      headerName: "Completion Signal",
      width: 100,
      type: "string",
    },
    {
      field: "shipped_file_type",
      headerName: "File Type",
      width: 100,
      type: "string",
    },
    {
      field: "volume",
      headerName: "Volume",
      type: "number",
      width: 110,
      valueGetter: getNumberValue,
      renderCell: renderNumberCell,
    },
    {
      field: "pct_volume_change",
      aggregable: false,
      headerName: "Pct Volume Change",
      type: "number",
      width: 110,
      valueGetter: getNumberValue,
      renderCell: renderPercentCell,
    },
    {
      field: "file_size_kb",
      headerName: "File Size",
      type: "number",
      width: 110,
      valueGetter: getNumberValue,
      renderCell: renderFileSizeCell,
    },
    {
      field: "pct_file_size_change",
      aggregable: false,
      headerName: "Pct File Size Change",
      type: "number",
      width: 110,
      valueGetter: getNumberValue,
      renderCell: renderPercentCell,
    },
    {
      field: "volume_upgrade_scheduled",
      headerName: "Volume Upgrade Scheduled",
      width: 110,
      type: "boolean",
      renderCell: (params) => <TableCellBooleanIcon value={params.value} />,
    },
  ];

  return (
    <GaneshDataGrid
      loading={isLoading}
      data={data}
      columns={columns_def}
      gridId="deliveryHistoryGrid"
      defaultGridState={defaultGridState}
    />
  );
};

export default DeliveryHistoryTable;
